import React from "react";
import composeRefs from "@seznam/compose-react-refs";
import { Draggable } from "react-beautiful-dnd";
import { formatSeconds } from "../epg-editor/utils/format-seconds";
import { getSecondsFromHis } from "../epg-editor/utils/create-ad-breaks";
import { useSchedulerContext } from "../../providers/scheduler-context";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import StraightIcon from "@mui/icons-material/Straight";
import ReactInputMask from "react-input-mask";
import TemplateAddBlockButton from "./template-add-block-button";
import { getMaximumDuration } from "../../common/programs/utils.js";
import useResizableSchedulerContent from "../../hooks/scheduler/use-resizable-scheduler-content.jsx";

function DraggableTemplateItem({
  item,
  droppableId,
  segment,
  updateDuration,
  // onItemClick,
  onItemDoubleClick,
  removeItem,
  secondsPerSection,
  pixelsPerSection,
  index,
  getHeightFromSeconds,
  smallDuration,
  smallOffset,
  addBlockThreshold,
  segmentCanInsert = true,
  prependMargin = 0,
  isLastItem = false,
  addBlock = () => {},
  openContextMenu = () => {},
}) {
  const [isResizing, setIsResizing] = React.useState(false);
  const [showResizeInput, setResizeInput] = React.useState(false);
  const [itemResizedDuration, setItemResizedDuration] = React.useState(
    formatSeconds(item.__gstvMeta.total_duration_seconds),
  );
  const resizingDuration = React.useRef();
  const resizingTill = React.useRef();
  const itemRef = React.useRef();
  const resizeInputRef = React.useRef();
  const { getRenderTime } = useSchedulerContext();
  const { onMouseDown } = useResizableSchedulerContent({
    content: item,
    contentRef: itemRef,
    updateDuration,
    segment,
    setIsResizing,
    onClick: onResizeClick,
    pixelsPerSection,
    secondsPerSection,
    getHeightFromSeconds,
    formatTimeForTimezone: getRenderTime,
    resizingLabelRef: resizingTill,
    resizingDurationRef: resizingDuration,
  });

  // ensure programResizedDuration is always up to date
  React.useEffect(() => {
    setItemResizedDuration(formatSeconds(item.__gstvMeta.total_duration_seconds));
  }, [item.__gstvMeta.total_duration_seconds]);

  function programExceedsSegmentLength(segmentEnd, itemEnd) {
    return itemEnd > segmentEnd;
  }

  function onResizeInputKeyPress(e) {
    if (e.key === "Enter") {
      commitResize(getSecondsFromHis(resizeInputRef.current.value));
    }

    if (e.key === "Escape") {
      cancelResize();
    }
  }

  function onResizeClick() {
    setResizeInput(true);
    setIsResizing(true);

    resizeInputRef.current.focus();
    resizeInputRef.current.addEventListener("keydown", onResizeInputKeyPress);

    window.addEventListener("keypress", onResizeInputKeyPress);
    window.addEventListener("mouseup", cancelResize);
  }

  function cancelResize() {
    setResizeInput(false);
    setIsResizing(false);
    window.removeEventListener("mouseup", cancelResize);
  }

  function updateProgramResizedDuration(duration) {
    // resize duration cannot be longer than program, and cannot be less han 0
    if (
      getSecondsFromHis(duration) > item.__gstvMeta.original_total_duration_seconds ||
      getSecondsFromHis(duration) < 0
    ) {
      setItemResizedDuration(formatSeconds(item.__gstvMeta.original_total_duration_seconds));
      return;
    }

    setItemResizedDuration(duration);
  }

  function commitResize(commitValue = null) {
    const nextDuration = commitValue !== null ? commitValue : getSecondsFromHis(itemResizedDuration);
    updateDuration(item.drag_id, nextDuration);
    closeResizeInput();
  }

  function closeResizeInput() {
    resizeInputRef.current.removeEventListener("keypress", onResizeInputKeyPress);
    window.removeEventListener("keypress", onResizeInputKeyPress);
    window.removeEventListener("mouseup", cancelResize);
    setResizeInput(false);
    setIsResizing(false);
  }

  const isSmall = getHeightFromSeconds(item.__gstvMeta.total_duration_seconds) < smallDuration;
  const isTrimmed =
    Math.round(item.__gstvMeta.total_duration_seconds) <
    Math.round(getMaximumDuration(item.__gstvMeta.original_duration_seconds, item.__gstvMeta.ad_breaks));
  let transformStyle = {};

  if (isSmall) {
    const offsets = [-150, -50, 50];
    transformStyle = {
      transform: `translateX(${offsets[smallOffset[0] + 1]}%) translateY(${-50 + 100 * smallOffset[1]}%)`,
    };
  }

  return (
    <div className="vertical-scheduler__program">
      <Draggable
        key={item.drag_id}
        draggableId={`${droppableId}-segment-${index}-${item.drag_id}`}
        index={index}
        isDragDisabled={isResizing}
      >
        {(provided) => {
          // programRef.current = provided.innerRef.current;
          return (
            <div
              ref={composeRefs(itemRef, provided.innerRef)}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              style={{
                height: `${getHeightFromSeconds(item.__gstvMeta.total_duration_seconds)}px`,
                marginTop: `${prependMargin}px`,
                ...provided.draggableProps.style,
              }}
              onDoubleClick={() => onItemDoubleClick(item)}
              onContextMenu={(e) => openContextMenu(item, e)}
              className={`vertical-scheduler-program ${isSmall ? "vertical-scheduler-program--small" : ""} ${
                programExceedsSegmentLength(segment.end, item.till) ? "vertical-scheduler-program--exceeded" : ""
              } ${item.type === "template_block" ? "vertical-scheduler-program--template-block" : ""}`}
              // ${itemInactive(item) ? "vertical-scheduler-program--inactive" : ""}`}
              // {...(itemInactive(item)
              //   ? {
              //       "aria-label": "Program is inactive and must be removed.",
              //       "data-balloon-pos": "up",
              //     }
              //   : {
              //       onClick: onItemClick.bind(null, item),
              //     })}
            >
              {isSmall ? (
                <div
                  className={`vertical-scheduler-program__inner vertical-scheduler-program__inner--small ${item.type !== "promo" ? "vertical-scheduler-program__inner--primary" : ""}`}
                  style={transformStyle}
                >
                  <div className="vertical-scheduler-program__content">
                    <span>{getRenderTime(item.till)}</span>
                    <div className="vertical-scheduler-program__actions">
                      <button
                        className="btn--text-primary vertical-scheduler__remove-button"
                        onClick={() => {
                          removeItem(item);
                        }}
                      >
                        <CancelRoundedIcon />
                      </button>
                    </div>
                  </div>
                  {isLastItem && segmentCanInsert ? (
                    <TemplateAddBlockButton
                      isTemplateItem={true}
                      addBlock={addBlock}
                      addBlockThreshold={addBlockThreshold}
                      secondsPerSection={secondsPerSection}
                      pixelsPerSection={pixelsPerSection}
                    />
                  ) : null}
                </div>
              ) : (
                <React.Fragment>
                  <div className="vertical-scheduler-program__inner">
                    <div className="vertical-scheduler-program__overflow-container">
                      <div className="vertical-scheduler-program__content">
                        <span>{`${item.title} (${getRenderTime(item.since)} to `}</span>
                        {isResizing ? (
                          <span ref={resizingTill}>{getRenderTime(item.till)}</span>
                        ) : (
                          <span>{getRenderTime(item.till)}</span>
                        )}
                        {")"}
                      </div>
                      <div className="vertical-scheduler-program__actions">
                        <button
                          className="btn--text-primary vertical-scheduler__remove-button"
                          onClick={() => {
                            removeItem(item);
                          }}
                        >
                          <CancelRoundedIcon />
                        </button>
                      </div>
                    </div>
                    {isLastItem && segmentCanInsert ? (
                      <TemplateAddBlockButton
                        isTemplateItem={true}
                        addBlock={addBlock}
                        addBlockThreshold={addBlockThreshold}
                        secondsPerSection={secondsPerSection}
                        pixelsPerSection={pixelsPerSection}
                      />
                    ) : null}
                  </div>
                  {item.Type !== "promo" ? (
                    <div
                      className={`vertical-scheduler-program__bottom ${isResizing ? "vertical-scheduler-program__left--active" : ""} ${isTrimmed ? "vertical-scheduler-program__bottom--trimmed" : ""}`}
                    >
                      <div className="vertical-scheduler-program__bottom__left">
                        <button
                          className="btn btn--reset vertical-scheduler-program__bottom__btn"
                          onMouseDown={onMouseDown}
                        >
                          <span className="vertical-scheduler-program__info">
                            {isResizing ? (
                              <span
                                className={`vertical-scheduler-program__info__text vertical-scheduler-program__info__text--${isResizing ? "visible" : "hidden"}`}
                                ref={resizingDuration}
                              >
                                {getRenderTime(item.till)}
                              </span>
                            ) : null}
                            <span
                              className={`vertical-scheduler-program__info__text vertical-scheduler-program__info__text--${isResizing ? "hidden" : "visible"}`}
                            >
                              {getRenderTime(item.till)}
                            </span>
                          </span>
                          <span
                            className={`vertical-scheduler-program__resize ${isTrimmed ? "vertical-scheduler-program__resize--trimmed" : ""}`}
                          >
                            <StraightIcon />
                          </span>
                        </button>
                        <div
                          className={`vertical-scheduler-program__resize-input ${
                            showResizeInput ? "vertical-scheduler-program__resize-input--active" : ""
                          }`}
                        >
                          <ReactInputMask
                            mask={"99:99:99"}
                            value={itemResizedDuration}
                            onChange={(e) => {
                              updateProgramResizedDuration(e.target.value);
                            }}
                            ref={resizeInputRef}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </React.Fragment>
              )}
            </div>
          );
        }}
      </Draggable>
    </div>
  );
}

export default DraggableTemplateItem;
